import { html, repeat, ref, when } from '@microsoft/fast-element';
import type { MarketDepthQuery } from './market-depth-query';
import { sync } from '@genesislcap/foundation-utils';
import { CONTRACT_TYPE, METHOD_EXECUTION, PRODUCT_TYPE, TECHNOLOGY } from '../../../../utils';
import { eventDetail } from '../../../../utils/formatting';

export const MarketDepthQueryTemplate = html<MarketDepthQuery>`
  <div class="container">
    <div class="dialog-content">
      <form class="market-depth-criteria-form">
        <div class="form-control">
          <label>ISO</label>
          <cascade-multi-select
            name="isoElement"
            class="card-version-market-depth"
            ${ref('isoElement')}
            @registeredClickCallback="${(x, c) => x.isoSelectionChanged(eventDetail(c))}"
          ></cascade-multi-select>
        </div>

        <div class="form-control">
          <label class="form-label">Technology</label>
          <zero-segmented-control
            appearance="primary"
            orientation="horizontal"
            :value="${sync((x) => x.techTypeValue)}"
            ${ref('technologyTypeElement')}
          >
            <zero-segmented-item
              @click=${(x) => x.technologyChanged(TECHNOLOGY.WIND)}
              class="custom-segmented-item"
              value="${TECHNOLOGY.WIND}"
              :current-checked=${(x) =>
                x.techTypeValue && x.techTypeValue === TECHNOLOGY.WIND}
            >
              Wind
            </zero-segmented-item>
            <zero-segmented-item
              @click=${(x) => x.technologyChanged(TECHNOLOGY.BOTH)}
              class="custom-segmented-item"
              value="${TECHNOLOGY.BOTH}"
              :current-checked=${(x) =>
                x.techTypeValue && x.techTypeValue === TECHNOLOGY.BOTH}
            >
              Both
            </zero-segmented-item>
            <zero-segmented-item
              @click=${(x) => x.technologyChanged(TECHNOLOGY.SOLAR)}
              class="custom-segmented-item"
              value="${TECHNOLOGY.SOLAR}"
              :current-checked=${(x) =>
                x.techTypeValue && x.techTypeValue === TECHNOLOGY.SOLAR}
            >
              Solar
            </zero-segmented-item>
          </zero-segmented-control>
        </div>

        <div class="form-control">
          <label>Product Type</label>
          <zero-select
            class="input-control custom-size-select"
            :value=${sync((x) => x.productTypeValue)}
            @change="${(x, c) => x.updateCriteria()}"
          >
            <zero-option value="">Select Product Type</zero-option>
            ${when(
              (x) => x.companyService.isSefParticipantAgreement(),
              html`
                <zero-option value="${() => PRODUCT_TYPE.VIRTUAL_PPA}">Virtual PPA</zero-option>
              `,
            )}
            ${when(
              (x) =>
                x.methodOfExecutionValue != METHOD_EXECUTION.MINI_POST_BUILD_VPPA_AUTO_EXECUTED,
              html`
                <zero-option value="${() => PRODUCT_TYPE.PHYSICAL_PPA}">Physical PPA</zero-option>
              `,
            )}
            ${when(
              (x) =>
                x.methodOfExecutionValue != METHOD_EXECUTION.MINI_POST_BUILD_VPPA_AUTO_EXECUTED,
              html`
                <zero-option value="${() => PRODUCT_TYPE.RECS}">RECs</zero-option>
              `,
            )}
          </zero-select>
        </div>

        <div class="form-control">
          <label class="form-label">Method of Execution</label>
          <zero-segmented-control
            appearance="primary"
            orientation="horizontal"
            :value="${sync((x) => x.methodOfExecutionValue)}"
            ${ref('methodOfExecutionElement')}
          >
            <zero-segmented-item
              @click=${(x) => x.methodOfExecutionChanged(METHOD_EXECUTION.RFQ)}
              class="custom-segmented-item"
              value="${METHOD_EXECUTION.RFQ}"
              :current-checked=${(x) =>
                x.methodOfExecutionValue !== null &&
                x.methodOfExecutionValue === METHOD_EXECUTION.RFQ}
            >
              RFQ
            </zero-segmented-item>
            <zero-segmented-item
              @click=${(x) =>
                x.methodOfExecutionChanged(METHOD_EXECUTION.MINI_POST_BUILD_VPPA_AUTO_EXECUTED)}
              class="custom-segmented-item"
              value="${METHOD_EXECUTION.MINI_POST_BUILD_VPPA_AUTO_EXECUTED}"
              :current-checked=${(x) =>
                x.methodOfExecutionValue !== null &&
                x.methodOfExecutionValue === METHOD_EXECUTION.MINI_POST_BUILD_VPPA_AUTO_EXECUTED}
              ?disabled=${(x) =>
                x.productTypeValue === PRODUCT_TYPE.RECS ||
                x.productTypeValue === PRODUCT_TYPE.PHYSICAL_PPA}
            >
              Auto-Executed
            </zero-segmented-item>
          </zero-segmented-control>
        </div>

        <div class="form-control">
          <label class="form-label">Contract Type</label>
          <zero-segmented-control
            appearance="primary"
            orientation="horizontal"
            :value="${sync((x) => x.contractTypeValue)}"
            ${ref('contractTypeElement')}
          >
            <zero-segmented-item
              @click=${(x) => x.contractTypeChanged(CONTRACT_TYPE.NEW_BUILD_VPPA)}
              class="custom-segmented-item"
              value="${CONTRACT_TYPE.NEW_BUILD_VPPA}"
              current-checked=${(x) => x.newBuildChecked}
              ?disabled=${(x) =>
                !x.productTypeValue ||
                !x.methodOfExecutionValue ||
                (x.methodOfExecutionValue &&
                  x.methodOfExecutionValue === METHOD_EXECUTION.MINI_POST_BUILD_VPPA_AUTO_EXECUTED)}
            >
              New Build
            </zero-segmented-item>
            <zero-segmented-item
              @click=${(x) => x.contractTypeChanged()}
              class="custom-segmented-item"
              value=""
              current-checked=${(x) => !x.newBuildChecked && !x.postBuildChecked && !x.contractTypeValue}
              ?disabled=${(x) =>
                !x.productTypeValue ||
                !x.methodOfExecutionValue ||
                (x.methodOfExecutionValue &&
                  x.methodOfExecutionValue === METHOD_EXECUTION.MINI_POST_BUILD_VPPA_AUTO_EXECUTED)}
            >
              Both
            </zero-segmented-item>
            <zero-segmented-item
              @click=${(x) => x.contractTypeChanged(CONTRACT_TYPE.POST_BUILD_VPPA)}
              class="custom-segmented-item"
              value="${CONTRACT_TYPE.POST_BUILD_VPPA}"
              current-checked=${(x) => x.postBuildChecked}
              ?disabled=${(x) =>
                !x.productTypeValue ||
                !x.methodOfExecutionValue ||
                (x.methodOfExecutionValue &&
                  x.methodOfExecutionValue === METHOD_EXECUTION.MINI_POST_BUILD_VPPA_AUTO_EXECUTED)}
            >
              Post Build
            </zero-segmented-item>
          </zero-segmented-control>
        </div>

        <div class="form-control">
          <label>Applicable REC Program</label>
          <cascade-multi-select
            name="recElement"
            class="card-version-market-depth"
            ${ref('recElement')}
            :isDisabled=${(x) => x.methodOfExecutionValue === METHOD_EXECUTION.MINI_POST_BUILD_VPPA_AUTO_EXECUTED}
            @registeredClickCallback="${(x, c) => x.recProgramSelected(eventDetail(c))}"
          ></cascade-multi-select>
        </div>

        <div class="form-control">
          <label>Settlement Point</label>
          <cascade-multi-select
            name="settlementPoints"
            class="card-version-market-depth"
            ${ref('settlementPointsElement')}
            :isDisabled=${(x) => x.settlementPointDisabled}
            @registeredClickCallback="${(x, c) => x.settlementPointSelectionChanged(eventDetail(c))}"
          ></cascade-multi-select>
        </div>

        <!-- Ranged inputs have to be in a form-contrl div and then grouped together by another div with class range-value -->
        <div class="form-control no-padding-bottom">
          <label class="form-label">Price</label>
          <div class="range-value">
            <zero-text-field
              class="input-control"
              type="number"
              placeholder="Enter Min..."
              min="0"
              oninput="this.value = !!this.value && this.value >= 0 ? this.value : null"
              :value=${sync((x) => x.priceMinValue)}
              @change="${(x) => x.updateCriteria()}"
              ${ref('priceMinElement')}
            ></zero-text-field>
            <zero-text-field
              class="input-control"
              type="number"
              placeholder="Enter Max..."
              min="0"
              oninput="this.value = !!this.value && this.value >= 0 ? this.value : null"
              :value=${sync((x) => x.priceMaxValue)}
              @change="${(x) => x.updateCriteria()}"
              ${ref('priceMaxElement')}
            ></zero-text-field>
          </div>
        </div>

        <!-- Ranged inputs have to be in a form-contrl div and then grouped together by another div with class range-value -->
        <div class="form-control no-padding-bottom">
          <label class="form-label">Tenor Date</label>
          <div class="range-value">
            <zero-text-field
              class="input-control"
              type="number"
              placeholder="Enter YYYY"
              min="2022"
              oninput="this.value = !!this.value && Math.abs(this.value) >= 0 ? Math.abs(this.value) : null"
              :value=${sync((x) => x.tenorDateMinValue)}
              @change="${(x) => x.updateCriteria()}"
              ${ref('tenorDateMinElement')}
            ></zero-text-field>
            <zero-text-field
              class="input-control"
              type="number"
              placeholder="Enter YYYY"
              min="2022"
              oninput="this.value = !!this.value && Math.abs(this.value) >= 0 ? Math.abs(this.value) : null"
              :value=${sync((x) => x.tenorDateMaxValue)}
              @change="${(x) => x.updateCriteria()}"
              ${ref('tenorDateMaxElement')}
            ></zero-text-field>
          </div>
        </div>
        <div class="form-control center-content">
          <zero-button appearance="secondary" @click=${(x) => x.resetCriteria()}>Reset</zero-button>
        </div>
      </form>
    </div>
  </div>
`;
